import { useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom, useStore } from 'jotai';

import { saveResearchModalV2Atom } from '../../job-explorer-restaurant-explorer.atoms';
import { mapInteractionStartedAtom } from '../../job-explorer-map.atoms';
import { recordJobExplorerModalAtom } from './job-explorer-save-research-v2';
import {
  SAVE_RESEARCH_AUTO_OPEN_DELAY_MS,
  canAutoOpenSaveResearch,
  markSaveResearchAutoOpened,
} from './save-research-auto-open-storage';

/**
 * Opens the «Salva la ricerca» modal once, 15 seconds after the user starts interacting with the
 * map, and then not again by itself for 7 days. Manual openings are not affected, but a manual
 * opening on this page cancels the automatic one: the user has already seen it.
 */
export const useAutoOpenSaveResearchModal = () => {
  const store = useStore();
  const mapInteractionStarted = useAtomValue(mapInteractionStartedAtom);
  const openSaveResearchModal = useSetAtom(saveResearchModalV2Atom);
  const recordAutoOpen = useSetAtom(recordJobExplorerModalAtom);
  const modalOpen = useAtomValue(saveResearchModalV2Atom);
  const scheduled = useRef(false);
  const autoOpening = useRef(false);
  const openedManually = useRef(false);

  useEffect(() => {
    if (modalOpen && !autoOpening.current) openedManually.current = true;
  }, [modalOpen]);

  useEffect(() => {
    if (!mapInteractionStarted || scheduled.current || openedManually.current) return;
    if (store.get(recordJobExplorerModalAtom) || !canAutoOpenSaveResearch()) return;
    scheduled.current = true;
    const timeout = setTimeout(() => {
      // The user may have opened it by hand meanwhile (even if already closed), or another tab may have shown it.
      if (openedManually.current || store.get(saveResearchModalV2Atom) || !canAutoOpenSaveResearch()) return;
      markSaveResearchAutoOpened();
      recordAutoOpen(true);
      autoOpening.current = true;
      openSaveResearchModal(true);
    }, SAVE_RESEARCH_AUTO_OPEN_DELAY_MS);
    return () => {
      clearTimeout(timeout);
      scheduled.current = false;
    };
  }, [mapInteractionStarted, openSaveResearchModal, recordAutoOpen, store]);
};
